import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialVectorOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {vec3} from "#material/types/vector"
import {z} from "zod/v4-mini"
import type {Space} from "#material/nodes/vector/displacement/validation"
import {space} from "#material/nodes/vector/displacement/validation"

export interface DisplacementSockets extends MaterialNodeSockets {
    inputs: {
        height: "float" // found in inputs: color,float,vector; found in settings: float; inferred from data: float
        midlevel: "float" // found in inputs: -; found in settings: float; inferred from data: float
        normal: "normal" // found in inputs: color,normal; found in settings: vector; inferred from data: float3
        scale: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settings: {
        displacement: "vector" // found in inputs: -; found in settings: vector; inferred from data: float3
        height: "float" // found in inputs: color,float,vector; found in settings: float; inferred from data: float
        midlevel: "float" // found in inputs: -; found in settings: float; inferred from data: float
        normal: "normal" // found in inputs: color,normal; found in settings: vector; inferred from data: float3
        scale: "float" // found in inputs: -; found in settings: float; inferred from data: float
    }
    settingsExtensions: {
        space?: Space // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        displacement: "vector"
    }
}

export const displacementDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeDisplacement,
    },
    nodeClass: "Displacement",
    ui: {
        label: "Displacement",
        color: "#6e5eb2",
        items: [
            {name: "displacement", label: "Displacement", valueType: "vector", output: true},
            {name: "height", label: "Height", valueType: "float", input: true, setting: true, defaultValue: 0},
            {name: "midlevel", label: "Midlevel", valueType: "float", input: true, setting: true, defaultValue: 0.5},
            {name: "scale", label: "Scale", valueType: "float", input: true, setting: true, defaultValue: 1},
            {name: "normal", label: "Normal", valueType: "normal", input: true, setting: true, defaultValue: {x: 0, y: 0, z: 0}},
            {
                name: "space",
                defaultValue: "OBJECT",
                label: "Space",
                valueType: "string",
                options: [
                    {label: "Object", value: "OBJECT"},
                    {label: "World", value: "WORLD"},
                ],
                input: false,
                setting: true,
            },
        ],
        support: {
            cycles: true,
            three: false,
        },
    },
    validation: {
        returns: z.strictObject({
            displacement: materialVectorOutput,
        }),
        parameters: z.strictObject({
            inputs: z.strictObject({
                height: optionalMaterialInput,
                midlevel: optionalMaterialInput,
                normal: optionalMaterialInput,
                scale: optionalMaterialInput,
            }),
            settings: z.strictObject({
                displacement: z.optional(vec3),
                height: z.optional(z.number()),
                midlevel: z.optional(z.number()),
                normal: z.optional(vec3),
                scale: z.optional(z.number()),
                space: z.optional(space),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<DisplacementSockets>
